/**
 * useChartTheme Hook
 *
 * Shared chart color tokens derived from the active MUI theme. Chart code must
 * take every color from here — no hex literals in chart props (theme-tokens-only
 * rule); `useTheme()` re-renders charts on light/dark mode switches.
 *
 * Import via the direct subpath `@modules/ui-components/hooks/useChartTheme`.
 * Deliberately NOT exported from the hooks barrel: barrel imports break
 * builder-service jest (known monorepo gotcha).
 */
import { useTheme, alpha } from '@mui/material/styles';

export interface ChartTheme {
  /** Categorical series palette (order = series index). */
  series: string[];
  primary: string;
  positive: string;
  negative: string;
  warning: string;
  info: string;
  secondary: string;
  grid: string;
  axisTick: { fill: string; fontSize: number };
  /** Reference lines / release markers. */
  reference: string;
  /** Hover cursor band fill. */
  cursorFill: string;
}

export function useChartTheme(): ChartTheme {
  const theme = useTheme();
  return {
    series: [
      theme.palette.primary.main,
      theme.palette.success.main,
      theme.palette.error.main,
      theme.palette.info.main,
      theme.palette.warning.main,
      theme.palette.secondary.main,
    ],
    primary: theme.palette.primary.main,
    positive: theme.palette.success.main,
    negative: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
    secondary: theme.palette.secondary.main,
    grid: theme.palette.divider,
    axisTick: { fill: theme.palette.text.secondary, fontSize: 12 },
    reference: alpha(theme.palette.text.primary, 0.4),
    cursorFill: alpha(theme.palette.primary.main, 0.08),
  };
}
